import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Link, NavLink, N_, useLang, useT } from "@/i18n";
import LanguageSwitcher from "@/components/public/LanguageSwitcher";
import { Menu, X, CalendarCheck, Instagram, Facebook, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { has } from "@/lib/api";

export const NAV = [
  { to: "/", label: N_("Accueil"), id: "home" },
  { to: "/appartements", label: N_("Nos appartements"), id: "apartments" },
  { to: "/sejour-en-groupe", label: N_("Séjour en groupe"), id: "group" },
  { to: "/decouvrir-le-queyras", label: N_("Découvrir le Queyras"), id: "queyras" },
  { to: "/galerie", label: N_("Galerie"), id: "gallery" },
  { to: "/avis-voyageurs", label: N_("Avis voyageurs"), id: "reviews" },
  { to: "/infos-pratiques", label: N_("Infos pratiques"), id: "practical" },
  { to: "/contact", label: N_("Contact"), id: "contact" },
];

export function Brand({ light = false, className = "" }) {
  const { settings } = useSite();
  const t = useT();
  const logo = light && settings.logo_light_url ? settings.logo_light_url : settings.logo_url;
  return (
    <Link to="/" data-testid="nav-brand-logo" className={`flex items-center gap-3 shrink-0 ${className}`} aria-label={settings.site_name || t("Accueil")}>
      {logo && <img src={logo} alt={settings.site_name || "Le Chalet Badouille Izoard"} className="h-[72px] sm:h-[88px] w-auto object-contain" loading="eager" />}
      <span className={`leading-tight ${logo ? "hidden md:block xl:hidden" : "block"}`}>
        <span className={`block font-serif text-[13px] sm:text-sm uppercase tracking-[0.14em] whitespace-nowrap ${light ? "text-ecru" : "text-forest"}`}>Le Chalet<br />Badouille<br />Izoard</span>
        <span className={`block text-[9px] uppercase tracking-[0.18em] whitespace-nowrap mt-0.5 ${light ? "text-ecru/60" : "text-sage"}`}>Arvieux · Queyras</span>
      </span>
    </Link>
  );
}

function Header() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { settings } = useSite();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  return (
    <>
    <header className="sticky top-0 z-50 bg-ecru/90 backdrop-blur-md border-b border-sand/70" data-testid="site-header">
      <div className="container-site xl:max-w-[1400px] h-20 sm:h-24 flex items-center justify-between gap-4">
        <Brand />
        <nav className="hidden xl:flex items-center gap-3 2xl:gap-4" aria-label={t("Navigation principale")}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.id}`} className={({ isActive }) => `link-nav ${isActive ? "active" : ""}`}>{t(n.label)}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher testIdPrefix="lang" />
          <Link to="/demande-de-disponibilite" data-testid="nav-availability-cta" className="btn-primary hidden sm:inline-flex whitespace-nowrap"><CalendarCheck size={16} /> {t("Demander une disponibilité")}</Link>
          <button data-testid="mobile-menu-trigger" onClick={() => setOpen(!open)} aria-label={open ? t("Fermer le menu") : t("Ouvrir le menu")} aria-expanded={open} className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-sand text-forest hover:bg-cream transition-colors">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>
      {open && (
        <div className="xl:hidden fixed inset-x-0 top-20 sm:top-24 bottom-0 bg-ecru z-40 overflow-y-auto fade-up" data-testid="mobile-menu">
          <nav className="container-site py-8 flex flex-col gap-1" aria-label={t("Navigation mobile")}>
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-link-${n.id}`} className={({ isActive }) => `font-serif text-2xl py-3 border-b border-sand/60 ${isActive ? "text-forest" : "text-forest/70"}`}>{t(n.label)}</NavLink>
            ))}
            <Link to="/demande-de-disponibilite" data-testid="mobile-availability-cta" className="btn-primary mt-6 w-full py-4 text-base"><CalendarCheck size={18} /> {t("Demander une disponibilité")}</Link>
            <LanguageSwitcher size="lg" className="mt-6 justify-center gap-3" testIdPrefix="mobile-lang" />
            {(has(settings.phone) || has(settings.email)) && (
              <div className="mt-8 text-sm text-charcoal/70 space-y-2">
                {has(settings.phone) && <a href={`tel:${settings.phone}`} className="flex items-center gap-2"><Phone size={14} /> {settings.phone}</a>}
                {has(settings.email) && <a href={`mailto:${settings.email}`} className="flex items-center gap-2"><Mail size={14} /> {settings.email}</a>}
              </div>
            )}
          </nav>
        </div>
      )}
    </>
  );
}

function Footer() {
  const t = useT();
  const { settings, content } = useSite();
  const social = settings.social || {};
  return (
    <footer className="bg-forest-950 text-ecru/80" data-testid="site-footer">
      <div className="container-site py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          {settings.logo_url ? (
            <Link to="/" className="inline-block bg-ecru rounded-2xl p-3 shadow-sm" aria-label={settings.site_name || "Le Chalet Badouille Izoard"} data-testid="footer-logo">
              <img src={settings.logo_url} alt={settings.site_name || "Le Chalet Badouille Izoard"} className="h-28 w-auto object-contain" loading="lazy" />
            </Link>
          ) : <p className="font-serif text-2xl uppercase tracking-[0.1em] text-ecru">Le Chalet Badouille<br />Izoard</p>}
          <p className="mt-3 text-sm text-ecru/60">Arvieux · Queyras · Hautes-Alpes</p>
          {has(content.footer?.text) && <p className="mt-5 text-sm leading-relaxed max-w-sm text-ecru/70">{content.footer.text}</p>}
          {has(settings.address_line1) && <address className="mt-5 text-sm not-italic text-ecru/60">{settings.address_line1}<br />{settings.address_line2}</address>}
        </div>
        <div className="md:col-span-3">
          <p className="eyebrow text-ecru/50 mb-4">{t("Navigation")}</p>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-ecru transition-colors">{t(n.label)}</Link></li>)}
            <li><Link to="/demande-de-disponibilite" className="hover:text-ecru transition-colors">{t("Demander une disponibilité")}</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="eyebrow text-ecru/50 mb-4">{t("Contact")}</p>
          <ul className="space-y-2 text-sm">
            {has(settings.phone) && <li><a href={`tel:${settings.phone}`} className="flex items-center gap-2 hover:text-ecru" data-testid="footer-phone"><Phone size={14} /> {settings.phone}</a></li>}
            {has(settings.email) && <li><a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-ecru" data-testid="footer-email"><Mail size={14} /> {settings.email}</a></li>}
            {!has(settings.phone) && !has(settings.email) && <li><Link to="/contact" className="hover:text-ecru">{t("Formulaire de contact")}</Link></li>}
          </ul>
          {(has(social.instagram) || has(social.facebook)) && (
            <div className="flex gap-3 mt-6">
              {has(social.instagram) && <a href={social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-ecru/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"><Instagram size={16} /></a>}
              {has(social.facebook) && <a href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-ecru/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"><Facebook size={16} /></a>}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-ecru/10">
        <div className="container-site py-6 flex flex-col sm:flex-row gap-3 justify-between text-xs text-ecru/50">
          <p>© {new Date().getFullYear()} Le Chalet Badouille Izoard{settings.signature ? ` · ${settings.signature}` : ""}</p>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-ecru">{t("Mentions légales")}</Link>
            <Link to="/politique-de-confidentialite" className="hover:text-ecru">{t("Politique de confidentialité")}</Link>
            <Link to="/admin" className="hover:text-ecru" data-testid="nav-admin-link">{t("Espace propriétaire")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const t = useT();
  const lang = useLang();
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1" key={lang}>
        <Outlet />
      </main>
      <Footer />
      {!/\/(demande-de-disponibilite|reservation|laisser-un-avis)/.test(pathname) && <Link to="/demande-de-disponibilite" data-testid="mobile-sticky-cta" className="sm:hidden fixed bottom-4 inset-x-4 z-40 btn-primary py-4 shadow-xl shadow-forest/30"><CalendarCheck size={18} /> {t("Demander une disponibilité")}</Link>}
    </div>
  );
}
